import type { ApiListingLocationType } from '#/api/types/ApiListingLocationType';
import { Icon } from '#/components/Icon/Icon';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { Tag } from '#/components/Tag/Tag';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingLocationLabel } from '#/modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { ListingLocationTypeLabel } from '#/modules/listing/components/ListingLocationTypeLabel/ListingLocationTypeLabel';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import type { SearchAlgoliaImportedSource } from '#/modules/search/algolia/types/SearchAlgoliaImportedSource';
import {
  SearchHitInfoFooterHolder,
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
} from '#/modules/search/components/Hit/Info/SearchHitInfo.styled';
import { SearchHitApplicationStatus } from '#/modules/search/components/Hit/SearchHitApplicationStatus';
import { formatSearchHitDate } from '#/modules/search/helpers/formatSearchHitDate';
import type {
  DateString,
  DatetimeString,
  IanaTimezone,
  TimeString,
} from '#/types/TypedDateString';
import { EVENT_IMPORTED_TYPE_MAP } from '#/utils/constants/general/eventImportedTypeMap';
import { VOLOP_IMPORTED_TYPE_MAP } from '#/utils/constants/general/volopImportedTypeMap';
import {
  safeUtf8StringSlice,
  utf8StringLength,
} from '#/utils/string/safeUtf8String';

import {
  SearchImportedHitInfoLabels,
  SearchImportedHitInfoSourceHolder,
} from './SearchImportedHitInfo.styled';

type Props = {
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: DatetimeString;
  } | null;
  startDate: DateString | null;
  startTime: TimeString | null;
  endDate: DateString | null;
  endTime: TimeString | null;
  timezone: IanaTimezone | null;
  isPostedAnonymously: boolean;
  location: string | null;
  locationType: ApiListingLocationType | null;
  name: string;
  orgName: string | null;
  orgType: string | null;
  showLabel: boolean;
  source: SearchAlgoliaImportedSource;
};

export function SearchImportedHitInfo({
  application,
  startDate,
  startTime,
  endDate,
  endTime,
  timezone,
  isPostedAnonymously,
  location,
  locationType,
  name,
  orgName,
  orgType,
  showLabel,
  source,
}: Props) {
  const { i18n, locale } = useI18n();

  const hasFooter = Boolean(application);

  return (
    <SearchHitInfoHolder>
      {showLabel && (
        <div style={{ marginBottom: 14 }}>
          <ListingTypeLabel listingType="IMPORTED" orgType={null} />
        </div>
      )}

      <SearchHitInfoHeader>
        <SearchHitInfoHeaderHeading>
          <div>
            <span data-qa-id="search-result-link">
              {safeUtf8StringSlice(name, 0, 120)}
            </span>
            {utf8StringLength(name) > 120 && <>&hellip;</>}
          </div>

          {isPostedAnonymously && (
            <div
              style={{
                verticalAlign: 'middle',
                display: 'inline-block',
                marginLeft: '4px',
              }}
            >
              <LegacyTooltip width={360}>
                {i18n(
                  `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                )}
              </LegacyTooltip>
            </div>
          )}
        </SearchHitInfoHeaderHeading>

        {(orgType === 'CORPORATION' || orgName) && (
          <SearchHitInfoHeaderOrgName>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {orgName}
            </div>
          </SearchHitInfoHeaderOrgName>
        )}
      </SearchHitInfoHeader>

      <SearchImportedHitInfoLabels>
        {locationType && (
          <ListingLocationTypeLabel
            listingType="IMPORTED"
            locationType={locationType}
          />
        )}

        {location && locationType && (
          <ListingLocationLabel
            locationType={locationType}
            location={location}
          />
        )}

        {startDate && timezone && (
          <Tag size="micro" prefix={<Icon name="calendar" size={14} />}>
            {formatSearchHitDate({
              locale,
              startDate,
              startTime,
              endDate,
              endTime,
              timezone,
            })}
          </Tag>
        )}
      </SearchImportedHitInfoLabels>

      <SearchImportedHitInfoSourceHolder>
        {i18n('Source')}:{' '}
        {source === 'NYCPARKS'
          ? EVENT_IMPORTED_TYPE_MAP[source]
          : VOLOP_IMPORTED_TYPE_MAP[source]}
      </SearchImportedHitInfoSourceHolder>

      {hasFooter && (
        <SearchHitInfoFooterHolder>
          {application && (
            <SearchHitApplicationStatus
              status={application.status}
              created={application.created}
            />
          )}
        </SearchHitInfoFooterHolder>
      )}
    </SearchHitInfoHolder>
  );
}
